import {CreatorError, creatorConfig, safeText, type CreatorProvider} from './creatorDomain.ts';
import {creatorJson} from './creatorHttp.ts';
import type {CreatorIdentity} from './creatorOAuth.ts';
export const CREATOR_READ_LIMITS = {daily: 60, monthly: 600} as const;
export const CREATOR_READS = [
  {provider: 'higgsfield', action: 'balance', fields: [], required: []},
  {provider: 'windsor', action: 'lists', fields: [], required: []},
  {provider: 'buffer', action: 'lists', fields: [], required: []},
  {provider: 'buffer', action: 'channels', fields: ['list_id'], required: ['list_id']},
  {provider: 'buffer', action: 'posts', fields: ['list_id', 'cursor'], required: ['list_id']},
] as const;
export function validateCreatorRead(body: Record<string, unknown>) {
  const spec = CREATOR_READS.find(s => s.provider === body.provider && s.action === body.action);
  if (!spec) throw new CreatorError('operation_not_allowed', 403);
  const allowed: readonly string[] = ['provider', 'action', ...spec.fields];
  if (Object.keys(body).some(k => !allowed.includes(k))) throw new CreatorError('invalid_request');
  for (const key of spec.required) if (typeof body[key] !== 'string' || !body[key]) throw new CreatorError(key + '_required');
  if (body.list_id !== undefined && (typeof body.list_id !== 'string' || !/^[A-Za-z0-9_-]{1,160}$/.test(body.list_id))) throw new CreatorError('invalid_list_id');
  if (body.cursor !== undefined && (typeof body.cursor !== 'string' || !/^[\x20-\x7e]{1,1024}$/.test(body.cursor))) throw new CreatorError('invalid_cursor');
  return spec;
}
function array(value: unknown): any[] {
  if (!Array.isArray(value)) throw new CreatorError('provider_protocol_error', 502);
  return value;
}
// Only these fixed reads are possible. No caller-supplied GraphQL, MCP names or URLs.
export async function creatorReadResult(provider: CreatorProvider, body: Record<string, unknown>, identity: CreatorIdentity,
  access: string, transport: typeof fetch = fetch) {
  validateCreatorRead(body);
  const account = {account_id: identity.id, account_name: identity.label};
  if (provider === 'higgsfield') {
    if (typeof identity.summary.credits !== 'number' || !Number.isFinite(identity.summary.credits) || identity.summary.credits < 0) throw new CreatorError('provider_read_failed', 502);
    return {item: {...account, credits: identity.summary.credits}, coverage: {state: 'complete', returned: 1, limit: 20}};
  }
  if (provider === 'windsor') {
    const connectors = array(identity.summary.connectors);
    const items = connectors.slice(0, 20).map(c => {
      if (!c || typeof c !== 'object' || Array.isArray(c) || typeof c.id !== 'string' || !c.id.trim() ||
        typeof c.accounts !== 'number' || !Number.isSafeInteger(c.accounts) || c.accounts < 0) throw new CreatorError('provider_protocol_error', 502);
      return {...account, id: safeText(c.id, 100), name: safeText(c.id, 100), accounts: c.accounts};
    });
    // This describes connector discovery only, never reporting data or all provider records.
    return {items, coverage: {state: connectors.length > 20 ? 'partial' : 'complete', returned: items.length, limit: 20}};
  }
  const query = async (document: string) => {
    const result = await creatorJson(creatorConfig.buffer.resource, {method: 'POST',
      headers: {Authorization: 'Bearer ' + access, 'Content-Type': 'application/json'}, body: JSON.stringify({query: document})}, transport);
    if (result.errors?.length || !result.data) throw new CreatorError('provider_read_failed', 502);
    return result.data;
  };
  const profile = (await query('query { account { id organizations { id name } } }')).account;
  if (profile?.id !== identity.id) throw new CreatorError('account_changed', 409);
  const organizations = array(profile.organizations);
  if (body.action === 'lists') return {items: organizations.slice(0, 20).map(o => ({...account, id: safeText(o.id), list_id: safeText(o.id), name: safeText(o.name)}))};
  if (!organizations.some(o => o.id === body.list_id)) throw new CreatorError('destination_not_available', 403);
  // JSON string literals are valid GraphQL string literals; inputs are also length/ASCII validated above.
  const organization = JSON.stringify(body.list_id);
  if (body.action === 'channels') {
    const result = await query('query { channels(input: {organizationId: ' + organization + '}) { id name service } }');
    return {items: array(result.channels).slice(0, 20).map(c => ({...account, id: safeText(c.id), list_id: body.list_id, name: safeText(c.name), service: safeText(c.service)}))};
  }
  const result = await query('query { posts(first: 20, after: ' + JSON.stringify(body.cursor ?? null) + ', input: {organizationId: ' + organization + '}) { edges { node { id text dueAt channelId status } } pageInfo { hasNextPage endCursor } } }');
  const posts = result.posts;
  const items = array(posts?.edges).slice(0, 20).map(e => {
    if (!e?.node || typeof e.node.id !== 'string') throw new CreatorError('provider_protocol_error', 502);
    return {...account, id: e.node.id, list_id: body.list_id, body_text: safeText(e.node.text, 10000),
      due_date: safeText(e.node.dueAt), channel_id: safeText(e.node.channelId), status: safeText(e.node.status)};
  });
  return {items, ...(posts.pageInfo?.hasNextPage && typeof posts.pageInfo.endCursor === 'string'
    ? {next_cursor: posts.pageInfo.endCursor.slice(0, 1024)} : {})};
}
